import React, { useMemo, useState } from 'react';
import { SlidersHorizontal, BarChart3, Info, ArrowUpRight } from 'lucide-react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Legend,
  ReferenceLine
} from 'recharts';

// MMM Demo: adstock + saturation response, weekly dummy data, simple optimizer

function seedRand(seed){ let t = seed | 0; return () => { t ^= t<<13; t ^= t>>17; t ^= t<<5; return Math.abs(t)/0x7fffffff; }; }
function formatTRY(x){ return '₺' + Math.round(x).toLocaleString('tr-TR'); }

const CHANNELS = [
  { id:'search', name:'Search', color:'#3b82f6', lambda:0.5, k:80000, beta:0.7, a:1.0 },
  { id:'meta', name:'Meta', color:'#22c55e', lambda:0.6, k:120000, beta:0.75, a:1.0 },
  { id:'tv', name:'TV', color:'#a78bfa', lambda:0.8, k:200000, beta:0.8, a:1.0 }
];

function adstockSeries(spendSeries, lambda){
  const out = [];
  let carry = 0;
  for (let i=0;i<spendSeries.length;i++){
    carry = spendSeries[i] + lambda * carry;
    out.push(carry);
  }
  return out;
}
function response(x, k, beta, a=1.0){
  // Hill-like saturation
  const xb = Math.pow(Math.max(0,x), beta);
  const kb = Math.pow(k, beta);
  return a * (xb / (kb + xb));
}

function buildWeeklyDummy(weeks=26){
  const rnd = seedRand(987654);
  const weeksArr = [];
  // base spend and seasonality
  let base = 100000;
  for (let i=0;i<weeks;i++){
    weeksArr.push({ week:`W${i+1}` });
  }
  const spendByCh = {};
  CHANNELS.forEach(ch => {
    let prev = (ch.id==='tv'? 140000 : ch.id==='meta'? 90000 : 70000);
    const arr = [];
    for (let i=0;i<weeks;i++){
      const shock = (rnd()-0.5) * (ch.id==='tv'? 20000 : 12000);
      const v = Math.max(10000, prev + shock);
      arr.push(v);
      prev = v;
    }
    spendByCh[ch.id] = arr;
  });
  // seasonality & baseline
  const baseline = [];
  for (let i=0;i<weeks;i++){
    const season = 1 + 0.08 * Math.sin((2*Math.PI*i)/13);
    const noise = 1 + (rnd()-0.5)*0.05;
    baseline.push(200000 * season * noise);
  }
  return { weeksArr, spendByCh, baseline };
}

export default function MMMDemo(){
  const { weeksArr, spendByCh, baseline } = useMemo(buildWeeklyDummy, []);
  const [extraBudget, setExtraBudget] = useState(100000); // ₺
  const [step, setStep] = useState(10000);
  const [priceDelta, setPriceDelta] = useState(0); // %
  const [promoDisc, setPromoDisc] = useState(0); // %

  const history = useMemo(() => {
    const result = weeksArr.map((w,i)=>({ week:w.week }));
    // adstock + response per channel
    const contribByCh = {};
    CHANNELS.forEach(ch => {
      const ads = adstockSeries(spendByCh[ch.id], ch.lambda);
      const resp = ads.map(x => response(x, ch.k, ch.beta, ch.a) * 800000); // scale for demo
      contribByCh[ch.id] = resp;
    });
    // price/promo modifiers to baseline (demo logic)
    const priceFactor = 1 + (priceDelta/100) * (-0.8); // price up -> demand down
    const promoFactor = 1 + (promoDisc/100) * 0.5;      // promo -> uplift
    const baseAdj = baseline.map(v => v * priceFactor * promoFactor);
    for (let i=0;i<result.length;i++){
      let sum = baseAdj[i];
      result[i].baseline = baseAdj[i];
      CHANNELS.forEach(ch => { result[i][ch.id] = contribByCh[ch.id][i]; sum += contribByCh[ch.id][i]; });
      result[i].revenue = sum;
    }
    return { weekly: result, contribByCh, baseAdj };
  }, [weeksArr, spendByCh, baseline, priceDelta, promoDisc]);

  // current spends (latest week) for optimizer
  const currentSpends = useMemo(()=> {
    const i = weeksArr.length - 1;
    const map = {};
    CHANNELS.forEach(ch=>{ map[ch.id] = spendByCh[ch.id][i]; });
    return map;
  }, [weeksArr, spendByCh]);

  function predictRevenueForSpend(ch, spend){
    // one-week static marginal prediction
    const ads = spend + ch.lambda * (currentSpends[ch.id]*0.8); // rough carry
    const resp = response(ads, ch.k, ch.beta, ch.a) * 800000;
    return resp;
  }
  function mroas(ch, spend, step){
    const r1 = predictRevenueForSpend(ch, spend);
    const r2 = predictRevenueForSpend(ch, spend + step);
    return (r2 - r1) / step;
  }
  function greedyAllocate(extra, step){
    const alloc = Object.fromEntries(CHANNELS.map(c=>[c.id,0]));
    const spends = { ...currentSpends };
    let inc = 0, remaining = extra;
    while(remaining >= step){
      let best = null, bestM = -Infinity;
      CHANNELS.forEach(ch=>{
        const m = mroas(ch, spends[ch.id], step);
        if (m > bestM){ bestM = m; best = ch.id; }
      });
      if (!best || bestM <= 0) break;
      alloc[best] += step;
      spends[best] += step;
      inc += bestM * step;
      remaining -= step;
    }
    return { allocation: alloc, totalIncremental: inc, newSpends: spends };
  }

  const reco = useMemo(()=>greedyAllocate(Number(extraBudget)||0, Number(step)||10000), [extraBudget, step, currentSpends]);

  const kpis = useMemo(()=>{
    const last = history.weekly[history.weekly.length-1];
    const curRevenue = last?.revenue || 0;
    const inc = Math.round(reco.totalIncremental);
    return {
      curRevenue,
      newRevenue: curRevenue + inc,
      incremental: inc
    };
  }, [history, reco]);

  // Response curve data for selected channel (show all stacked small charts)
  const responseDataByCh = useMemo(()=>{
    const map = {};
    CHANNELS.forEach(ch=>{
      const cur = currentSpends[ch.id];
      const max = cur*2 + 200000;
      const pts = [];
      const ticks = 30;
      for(let i=0;i<=ticks;i++){
        const s = (max * i)/ticks;
        pts.push({ spend:s, revenue: predictRevenueForSpend(ch, s) });
      }
      map[ch.id] = { data: pts, cur, newSpend: cur + (reco.allocation[ch.id]||0) };
    });
    return map;
  }, [currentSpends, reco]);

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-gray-900 flex items-center justify-center shadow-md">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-gray-900">Marketing Mix Modeling (Demo)</h1>
              <p className="text-sm text-gray-500 mt-1">Adstock + saturation response, weekly contributions, and budget optimization</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm w-24 text-gray-700">Price Δ%</span>
              <input type="range" min={-20} max={20} value={priceDelta} onChange={e=>setPriceDelta(Number(e.target.value))} />
              <span className="text-sm w-10 text-right">{priceDelta}%</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm w-24 text-gray-700">Promo %</span>
              <input type="range" min={0} max={40} value={promoDisc} onChange={e=>setPromoDisc(Number(e.target.value))} />
              <span className="text-sm w-10 text-right">{promoDisc}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">Current Weekly Revenue</div>
          <div className="text-2xl font-bold text-gray-900">{formatTRY(kpis.curRevenue)}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">Expected Incremental</div>
          <div className="text-2xl font-bold text-gray-900">{formatTRY(kpis.incremental)}</div>
          <div className="text-xs text-green-600 mt-1 flex items-center"><ArrowUpRight className="w-3 h-3 mr-1" />with +{formatTRY(extraBudget)}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">New Weekly Revenue</div>
          <div className="text-2xl font-bold text-gray-900">{formatTRY(kpis.newRevenue)}</div>
        </div>
      </div>

      {/* Optimizer Controls + Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-800 text-sm">Budget Optimization</h3>
          <div className="text-xs text-gray-500 flex items-center"><SlidersHorizontal className="w-3.5 h-3.5 mr-1" />Greedy allocator using MROAS</div>
        </div>
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Extra Budget</span>
            <input type="number" className="border border-gray-200 rounded px-2 py-1 text-sm w-32" value={extraBudget} onChange={e=>setExtraBudget(e.target.value)} step={10000} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Step</span>
            <select className="border border-gray-200 rounded px-2 py-1 text-sm" value={step} onChange={e=>setStep(e.target.value)}>
              <option value={5000}>₺5,000</option>
              <option value={10000}>₺10,000</option>
              <option value={20000}>₺20,000</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-gray-500">
                <th className="text-left font-medium py-2 pr-4">Channel</th>
                <th className="text-right font-medium py-2 pr-4">Current Spend</th>
                <th className="text-right font-medium py-2 pr-4">+Budget</th>
                <th className="text-right font-medium py-2 pr-4">New Spend</th>
                <th className="text-right font-medium py-2">Expected ΔRevenue</th>
              </tr>
            </thead>
            <tbody>
              {CHANNELS.map(ch=>{
                const add = reco.allocation[ch.id] || 0;
                const cur = currentSpends[ch.id];
                const newS = cur + add;
                const dRev = Math.max(0, predictRevenueForSpend(ch, newS) - predictRevenueForSpend(ch, cur));
                return (
                  <tr key={ch.id} className="border-t border-gray-100">
                    <td className="py-2 pr-4 text-gray-800">{ch.name}</td>
                    <td className="py-2 pr-4 text-right text-gray-800">{formatTRY(cur)}</td>
                    <td className="py-2 pr-4 text-right text-blue-700 font-medium">{add>0? `+${formatTRY(add)}` : '—'}</td>
                    <td className="py-2 pr-4 text-right text-gray-800">{formatTRY(newS)}</td>
                    <td className="py-2 text-right text-green-600">{dRev>0? `+${formatTRY(dRev)}` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Contributions over time */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-800 text-sm">Weekly Contribution Decomposition</h3>
          <div className="text-xs text-gray-500 flex items-center"><Info className="w-3.5 h-3.5 mr-1" />Baseline + channel contributions</div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={history.weekly} stackOffset="expand">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="week" stroke="#9ca3af" />
              <YAxis tickFormatter={(v)=>`${Math.round(v*100)}%`} stroke="#9ca3af" />
              <Tooltip formatter={(v, n)=>[typeof v === 'number'? `${Math.round(v*100)}%` : v, n]} />
              <Legend />
              <Area type="monotone" dataKey="baseline" name="Baseline" stackId="1" stroke="#64748b" fill="#cbd5e1" />
              {CHANNELS.map(ch=>(
                <Area key={ch.id} type="monotone" dataKey={ch.id} name={ch.name} stackId="1" stroke={ch.color} fill={ch.color} fillOpacity={0.6} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Response curves */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {CHANNELS.map(ch=>{
          const rc = responseDataByCh[ch.id];
          return (
            <div key={ch.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="text-xs text-gray-500 mb-2">{ch.name} — Response Curve</div>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsLineChart data={rc.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="spend" tickFormatter={(v)=>formatTRY(v)} stroke="#9ca3af" />
                    <YAxis tickFormatter={(v)=>formatTRY(v)} stroke="#9ca3af" />
                    <Tooltip formatter={(v,n)=>[formatTRY(v), n]} />
                    <Line type="monotone" dataKey="revenue" stroke={ch.color} strokeWidth={2} dot={false} />
                    <ReferenceLine x={rc.cur} stroke="#0ea5e9" strokeDasharray="3 3" label={{ value:'Current', position:'insideTopRight', fill:'#0ea5e9', fontSize:11 }} />
                    <ReferenceLine x={rc.newSpend} stroke="#10b981" strokeDasharray="3 3" label={{ value:'Proposed', position:'insideTopRight', fill:'#10b981', fontSize:11 }} />
                  </RechartsLineChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })}
      </div>

      {/* Methodology */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium text-gray-800 text-sm">Methodology</h3>
          <div className="text-xs text-gray-500">Adstock + saturation, baseline controls, greedy optimizer</div>
        </div>
        <ul className="text-xs text-gray-700 space-y-1">
          <li>Adstock: carry‑over with λ per channel; Saturation: Hill‑like diminishing returns.</li>
          <li>Baseline: seasonality + price/promo modifiers.</li>
          <li>Optimizer: distributes extra budget by marginal ROAS until step is exhausted.</li>
        </ul>
      </div>
    </div>
  );
}


